import { useState, useEffect, useRef, useCallback } from 'react';

// ─── Physical Constants ───────────────────────────────────────────────────────
const MU          = 3.986e14;
const RE          = 6371000;
const M_SAT       = 12;
const ISP         = 70;
const G0          = 9.81;
const J_BURN      = 0.313;
const DV_BURN     = J_BURN / M_SAT;          // 0.02608 m/s
const VE          = ISP * G0;                // 686.7 m/s exhaust velocity
const RHO_450     = 4.1e-12;
const CD          = 2.2;
const AREA        = 0.01;
const TOTAL_BURNS = 215;
const PHASE1_BURNS = 108;
const PHASE2_BURNS = TOTAL_BURNS - PHASE1_BURNS;
const ALT_START   = 450000;
const ALT_TARGET  = 460000;
const R_START     = RE + ALT_START;
const R_TARGET    = RE + ALT_TARGET;

// Chemical ISP for comparison
const ISP_CHEM    = 220;
const VE_CHEM     = ISP_CHEM * G0;

// Station-keeping
const SK_DV_PER_YEAR = 30; // m/s per year

// ─── Orbital Elements from State Vector ──────────────────────────────────────
function elements(x, y, vx, vy) {
  const r   = Math.sqrt(x * x + y * y);
  const v2  = vx * vx + vy * vy;
  const vr  = (x * vx + y * vy) / r;

  // Specific energy
  const eps = v2 / 2 - MU / r;
  const a   = -MU / (2 * eps);

  // Eccentricity vector components
  const ex  = (vy * (x * vy - y * vx) / MU) - x / r;
  const ey  = (-vx * (x * vy - y * vx) / MU) - y / r;
  const e   = Math.sqrt(ex * ex + ey * ey);

  const rApo  = a * (1 + e);
  const rPeri = a * (1 - e);

  return { a, e, rApo, rPeri, altApo: rApo - RE, altPeri: rPeri - RE, r, vr };
}

// ─── RK4 Integrator with Atmospheric Drag ────────────────────────────────────
function derivatives(state) {
  const { x, y, vx, vy } = state;
  const r  = Math.sqrt(x * x + y * y);
  const r3 = r * r * r;

  // Gravitational acceleration
  const ax_grav = -MU * x / r3;
  const ay_grav = -MU * y / r3;

  // Drag acceleration (simplified, assumes density at current altitude)
  const alt = r - RE;
  // Simple exponential atmosphere model centered near 450 km
  const rho = RHO_450 * Math.exp(-(alt - ALT_START) / 60000);
  const v   = Math.sqrt(vx * vx + vy * vy);
  const dragFactor = -0.5 * rho * CD * AREA / M_SAT * v;
  const ax_drag = dragFactor * vx;
  const ay_drag = dragFactor * vy;

  return {
    dx:  vx,
    dy:  vy,
    dvx: ax_grav + ax_drag,
    dvy: ay_grav + ay_drag,
  };
}

function rk4Drag(s, dt) {
  const k1 = derivatives(s);

  const s2 = {
    x:  s.x  + 0.5 * dt * k1.dx,
    y:  s.y  + 0.5 * dt * k1.dy,
    vx: s.vx + 0.5 * dt * k1.dvx,
    vy: s.vy + 0.5 * dt * k1.dvy,
  };
  const k2 = derivatives(s2);

  const s3 = {
    x:  s.x  + 0.5 * dt * k2.dx,
    y:  s.y  + 0.5 * dt * k2.dy,
    vx: s.vx + 0.5 * dt * k2.dvx,
    vy: s.vy + 0.5 * dt * k2.dvy,
  };
  const k3 = derivatives(s3);

  const s4 = {
    x:  s.x  + dt * k3.dx,
    y:  s.y  + dt * k3.dy,
    vx: s.vx + dt * k3.dvx,
    vy: s.vy + dt * k3.dvy,
  };
  const k4 = derivatives(s4);

  return {
    x:  s.x  + dt * (k1.dx  + 2 * k2.dx  + 2 * k3.dx  + k4.dx)  / 6,
    y:  s.y  + dt * (k1.dy  + 2 * k2.dy  + 2 * k3.dy  + k4.dy)  / 6,
    vx: s.vx + dt * (k1.dvx + 2 * k2.dvx + 2 * k3.dvx + k4.dvx) / 6,
    vy: s.vy + dt * (k1.dvy + 2 * k2.dvy + 2 * k3.dvy + k4.dvy) / 6,
  };
}

// ─── Orbit State Factory ──────────────────────────────────────────────────────
function makeOrbit() {
  const vy0 = Math.sqrt(MU / R_START);
  const T0  = 2 * Math.PI * Math.sqrt(R_START * R_START * R_START / MU);
  return {
    x:          R_START,
    y:          0,
    vx:         0,
    vy:         vy0,
    prevVr:     0,
    phase:      'phase1',
    burnCount:  0,
    dvTotal:    0,
    propMg:     0,
    fired:      false,
    fireTimer:  0,
    simTime:    0,
    trail:      [],
    apogee:     ALT_START / 1e3,
    perigee:    ALT_START / 1e3,
    apoHistory: [{ b: 0, alt: ALT_START / 1e3 }],
    periHistory:[{ b: 0, alt: ALT_START / 1e3 }],
    cooldown:   0,
    orbitalPeriod: T0,
    complete:   false,
  };
}

// ─── Step Function: integrate + burn detection ────────────────────────────────
function stepOrbit(s, dt, burnMode) {
  const ns = { ...s };

  // Integrate position and velocity
  const integrated = rk4Drag(
    { x: ns.x, y: ns.y, vx: ns.vx, vy: ns.vy },
    dt
  );
  ns.x  = integrated.x;
  ns.y  = integrated.y;
  ns.vx = integrated.vx;
  ns.vy = integrated.vy;
  ns.simTime += dt;

  // Compute radial velocity
  const r  = Math.sqrt(ns.x * ns.x + ns.y * ns.y);
  const vr = (ns.x * ns.vx + ns.y * ns.vy) / r;

  // Update cooldown
  if (ns.cooldown > 0) ns.cooldown -= dt;

  // Burn detection: vr crosses zero (descending: + → -)
  ns.fired = false;
  if (ns.cooldown <= 0 && ns.burnCount < TOTAL_BURNS) {
    const vrCrossing = ns.prevVr > 0.001 && vr <= 0.001;

    const doPhase1 = (burnMode === 'auto' || burnMode === 'phase1') &&
                     ns.burnCount < PHASE1_BURNS &&
                     ns.x > 0;                       // near perigee (right side)

    const doPhase2 = (burnMode === 'auto' || burnMode === 'phase2') &&
                     ns.burnCount >= PHASE1_BURNS &&
                     ns.burnCount < TOTAL_BURNS &&
                     ns.x < 0;                       // near apogee (left side)

    if (vrCrossing && (doPhase1 || doPhase2)) {
      // Apply prograde burn
      const v    = Math.sqrt(ns.vx * ns.vx + ns.vy * ns.vy);
      ns.vx += DV_BURN * ns.vx / v;
      ns.vy += DV_BURN * ns.vy / v;
      ns.burnCount += 1;
      ns.dvTotal   += DV_BURN;
      // Tsiolkovsky: dm = m*(1 - exp(-dv/Ve))
      const dm = M_SAT * (1 - Math.exp(-DV_BURN / VE));
      ns.propMg += dm * 1e6; // convert kg → mg

      ns.fired     = true;
      ns.fireTimer = 0.8;    // show thrust arrow for 0.8 sim-seconds

      // Cooldown = 80% of orbital period to prevent re-firing same pass
      const elems  = elements(ns.x, ns.y, ns.vx, ns.vy);
      const T      = 2 * Math.PI * Math.sqrt(Math.pow(elems.a, 3) / MU);
      ns.cooldown  = 0.8 * T;
      ns.orbitalPeriod = T;

      // Update phase
      if (ns.burnCount >= PHASE1_BURNS && ns.phase === 'phase1') {
        ns.phase = 'phase2';
      }
    }
  }

  // Decay fire timer
  if (ns.fireTimer > 0) ns.fireTimer -= dt;

  ns.prevVr = vr;

  // Compute current orbital elements
  const el = elements(ns.x, ns.y, ns.vx, ns.vy);
  ns.apogee  = el.altApo  / 1e3;
  ns.perigee = el.altPeri / 1e3;

  // Record history on each burn
  if (ns.fired) {
    ns.apoHistory  = [...ns.apoHistory,  { b: ns.burnCount, alt: ns.apogee }];
    ns.periHistory = [...ns.periHistory, { b: ns.burnCount, alt: ns.perigee }];
  }

  // Update trail (keep last 600 points)
  const trailColor = vr > 0 ? 'orange' : 'cyan';
  ns.trail = [...ns.trail, { x: ns.x, y: ns.y, color: trailColor }];
  if (ns.trail.length > 600) ns.trail = ns.trail.slice(-600);

  // Check completion
  if (ns.burnCount >= TOTAL_BURNS) ns.complete = true;

  return ns;
}

// ─── Canvas Drawing: Orbit View ───────────────────────────────────────────────
function drawOrbit(canvas, s) {
  if (!canvas) return;
  const ctx  = canvas.getContext('2d');
  const W    = canvas.width;
  const H    = canvas.height;
  const CX   = W / 2;
  const CY   = H / 2;
  // Scale: 460km orbit radius maps to 280 px
  const sc   = 280 / R_TARGET;

  ctx.clearRect(0, 0, W, H);

  // Background
  ctx.fillStyle = '#020810';
  ctx.fillRect(0, 0, W, H);

  // Starfield (static seed)
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  for (let i = 0; i < 120; i++) {
    const sx = ((i * 137 + 41) % W);
    const sy = ((i * 97  + 23) % H);
    const ss = (i % 3 === 0) ? 1.5 : 0.8;
    ctx.fillRect(sx, sy, ss, ss);
  }

  // Reference 450 km orbit (dashed grey)
  ctx.save();
  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = 'rgba(150,150,150,0.5)';
  ctx.lineWidth   = 1;
  ctx.beginPath();
  ctx.arc(CX, CY, R_START * sc, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.restore();

  // Target 460 km orbit (dashed yellow)
  ctx.save();
  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = 'rgba(255,220,0,0.6)';
  ctx.lineWidth   = 1.5;
  ctx.beginPath();
  ctx.arc(CX, CY, R_TARGET * sc, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.restore();

  // Draw current ellipse from orbital elements
  const el = elements(s.x, s.y, s.vx, s.vy);
  if (el.a > 0 && el.e < 1) {
    const a_px  = el.a * sc;
    const b_px  = el.a * Math.sqrt(1 - el.e * el.e) * sc;
    const c_px  = el.a * el.e * sc; // focus offset

    ctx.save();
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath();
    // Ellipse: translate so that left focus is at CX, CY
    // Earth is at one focus; orbit drawn centered on geometric center
    ctx.ellipse(CX - c_px, CY, a_px, b_px, 0, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.restore();

    // Perigee marker
    const periX = CX + (el.rPeri - el.a * el.e) * sc - c_px; // perigee is at right focus + rPeri - c
    // Simpler: perigee point is at (rPeri from focus, 0) = CX - c_px + rPeri*sc ... no, focus is at CX
    // Focus is at CX (Earth). Perigee is at distance rPeri from focus, in +x direction
    const periPx = CX + el.rPeri * sc;
    const apoPx  = CX - el.rApo  * sc;

    // Perigee dot
    ctx.save();
    ctx.fillStyle   = '#4488ff';
    ctx.shadowColor = '#4488ff';
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(periPx, CY, 5, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#aaccff';
    ctx.font      = 'bold 12px monospace';
    ctx.fillText('PERIGEE', periPx + 8, CY + 4);

    // Apogee dot
    ctx.save();
    ctx.fillStyle   = '#00ff88';
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(apoPx, CY, 5, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#88ffcc';
    ctx.font      = 'bold 12px monospace';
    ctx.fillText('APOGEE', apoPx - 65, CY - 10);

    // Phase arrows
    if (s.burnCount < PHASE1_BURNS) {
      // Green arrows at perigee side
      for (let i = 0; i < Math.min(s.burnCount, 6); i++) {
        const angle = (-0.15 + i * 0.06);
        const ax0   = CX + el.rPeri * sc * Math.cos(angle);
        const ay0   = CY - el.rPeri * sc * Math.sin(angle);
        ctx.save();
        ctx.strokeStyle = '#00ff88';
        ctx.lineWidth   = 1.5;
        ctx.beginPath();
        ctx.moveTo(ax0, ay0);
        ctx.lineTo(ax0, ay0 - 18);
        ctx.stroke();
        ctx.restore();
      }
    } else {
      // Orange arrows at apogee side
      for (let i = 0; i < Math.min(s.burnCount - PHASE1_BURNS, 6); i++) {
        const angle = Math.PI + (-0.15 + i * 0.06);
        const ax0   = CX + el.rApo * sc * Math.cos(angle);
        const ay0   = CY - el.rApo * sc * Math.sin(angle);
        ctx.save();
        ctx.strokeStyle = '#ff8800';
        ctx.lineWidth   = 1.5;
        ctx.beginPath();
        ctx.moveTo(ax0, ay0);
        ctx.lineTo(ax0, ay0 + 18);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  // Trail
  for (let i = 1; i < s.trail.length; i++) {
    const pt  = s.trail[i];
    const ppt = s.trail[i - 1];
    const alpha = (i / s.trail.length) * 0.7;
    ctx.beginPath();
    ctx.strokeStyle = pt.color === 'orange'
      ? `rgba(255,140,0,${alpha})`
      : `rgba(0,200,255,${alpha})`;
    ctx.lineWidth = 1.5;
    ctx.moveTo(CX + ppt.x * sc, CY - ppt.y * sc);
    ctx.lineTo(CX + pt.x  * sc, CY - pt.y  * sc);
    ctx.stroke();
  }

  // Earth
  const earthR = 60;
  const earthGrad = ctx.createRadialGradient(CX - 10, CY - 10, 5, CX, CY, earthR);
  earthGrad.addColorStop(0, '#1a6bc7');
  earthGrad.addColorStop(0.5, '#0d4a8a');
  earthGrad.addColorStop(1, '#051f3a');
  ctx.save();
  ctx.fillStyle   = earthGrad;
  ctx.shadowColor = '#1a6bc7';
  ctx.shadowBlur  = 20;
  ctx.beginPath();
  ctx.arc(CX, CY, earthR, 0, 2 * Math.PI);
  ctx.fill();
  ctx.restore();

  // Earth label
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font      = 'bold 11px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('EARTH', CX, CY + 4);
  ctx.textAlign = 'left';

  // Satellite dot with glow
  const satX = CX + s.x * sc;
  const satY = CY - s.y * sc;
  ctx.save();
  ctx.shadowColor = s.fired || s.fireTimer > 0 ? '#ffaa00' : '#00ff88';
  ctx.shadowBlur  = 20;
  ctx.fillStyle   = s.fired || s.fireTimer > 0 ? '#ffdd00' : '#ffffff';
  ctx.beginPath();
  ctx.arc(satX, satY, 5, 0, 2 * Math.PI);
  ctx.fill();
  ctx.restore();

  // Thrust arrow
  if (s.fireTimer > 0 && s.fireTimer > -0.5) {
    const v = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
    const tx = s.vx / v;
    const ty = -s.vy / v; // canvas y is flipped
    ctx.save();
    ctx.strokeStyle = '#ff6600';
    ctx.lineWidth   = 3;
    ctx.shadowColor = '#ff6600';
    ctx.shadowBlur  = 15;
    ctx.beginPath();
    ctx.moveTo(satX, satY);
    ctx.lineTo(satX + tx * 40, satY + ty * 40);
    ctx.stroke();
    // Arrowhead
    const angle = Math.atan2(ty, tx);
    ctx.fillStyle = '#ff6600';
    ctx.beginPath();
    ctx.moveTo(satX + tx * 40, satY + ty * 40);
    ctx.lineTo(
      satX + tx * 40 - 10 * Math.cos(angle - 0.4),
      satY + ty * 40 - 10 * Math.sin(angle - 0.4)
    );
    ctx.lineTo(
      satX + tx * 40 - 10 * Math.cos(angle + 0.4),
      satY + ty * 40 - 10 * Math.sin(angle + 0.4)
    );
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // Labels (450, 460 km)
  ctx.fillStyle = 'rgba(150,150,150,0.8)';
  ctx.font      = '11px monospace';
  ctx.fillText('450 km ref', CX + R_START * sc + 4, CY);
  ctx.fillStyle = 'rgba(255,220,0,0.8)';
  ctx.fillText('460 km target', CX + R_TARGET * sc + 4, CY - 12);

  // Phase label
  ctx.fillStyle = s.phase === 'phase1' ? '#00ff88' : '#ff8800';
  ctx.font      = 'bold 14px monospace';
  ctx.fillText(
    s.phase === 'phase1'
      ? `PHASE 1: Raising Apogee (${s.burnCount}/${PHASE1_BURNS} burns)`
      : s.complete
        ? 'TRANSFER COMPLETE - Station Keeping Mode'
        : `PHASE 2: Circularising (${s.burnCount - PHASE1_BURNS}/${PHASE2_BURNS} burns)`,
    10, 24
  );
}

// ─── Canvas Drawing: Progress Chart ──────────────────────────────────────────
function drawProgress(canvas, s) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W   = canvas.width;
  const H   = canvas.height;

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#030d1a';
  ctx.fillRect(0, 0, W, H);

  const pad  = { top: 30, right: 30, bottom: 40, left: 70 };
  const cW   = W - pad.left - pad.right;
  const cH   = H - pad.top  - pad.bottom;

  // Y range: 448 to 462 km
  const yMin = 448;
  const yMax = 462;

  const toX = (b)   => pad.left + (b / TOTAL_BURNS) * cW;
  const toY = (alt) => pad.top  + cH - ((alt - yMin) / (yMax - yMin)) * cH;

  // Grid lines
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth   = 1;
  for (let km = 448; km <= 462; km += 2) {
    const y = toY(km);
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + cW, y);
    ctx.stroke();
  }
  for (let b = 0; b <= TOTAL_BURNS; b += 50) {
    const x = toX(b);
    ctx.beginPath();
    ctx.moveTo(x, pad.top);
    ctx.lineTo(x, pad.top + cH);
    ctx.stroke();
  }

  // Target lines (dashed yellow at 460 km)
  ctx.save();
  ctx.setLineDash([5, 5]);
  ctx.strokeStyle = 'rgba(255,220,0,0.6)';
  ctx.lineWidth   = 1;
  const yTarget = toY(460);
  ctx.beginPath();
  ctx.moveTo(pad.left, yTarget);
  ctx.lineTo(pad.left + cW, yTarget);
  ctx.stroke();
  ctx.restore();

  // Phase boundary at burn 108
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = 'rgba(255,100,100,0.5)';
  ctx.lineWidth   = 1;
  const xPhase = toX(PHASE1_BURNS);
  ctx.beginPath();
  ctx.moveTo(xPhase, pad.top);
  ctx.lineTo(xPhase, pad.top + cH);
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = 'rgba(255,100,100,0.7)';
  ctx.font      = '11px monospace';
  ctx.fillText('Ph2', xPhase + 3, pad.top + 12);

  // Apogee line (green)
  if (s.apoHistory.length > 1) {
    ctx.save();
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth   = 2;
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur  = 4;
    ctx.beginPath();
    s.apoHistory.forEach((pt, i) => {
      const x = toX(pt.b);
      const y = toY(pt.alt);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.restore();
  }

  // Perigee line (cyan)
  if (s.periHistory.length > 1) {
    ctx.save();
    ctx.strokeStyle = '#00ddff';
    ctx.lineWidth   = 2;
    ctx.shadowColor = '#00ddff';
    ctx.shadowBlur  = 4;
    ctx.beginPath();
    s.periHistory.forEach((pt, i) => {
      const x = toX(pt.b);
      const y = toY(pt.alt);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.restore();
  }

  // Axes
  ctx.strokeStyle = 'rgba(255,255,255,0.4)';
  ctx.lineWidth   = 1;
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top);
  ctx.lineTo(pad.left, pad.top + cH);
  ctx.lineTo(pad.left + cW, pad.top + cH);
  ctx.stroke();

  // Y axis labels
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font      = '11px monospace';
  ctx.textAlign = 'right';
  for (let km = 448; km <= 462; km += 2) {
    ctx.fillText(`${km}`, pad.left - 5, toY(km) + 4);
  }
  ctx.textAlign = 'left';

  // X axis labels
  for (let b = 0; b <= TOTAL_BURNS; b += 50) {
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font      = '11px monospace';
    ctx.fillText(`${b}`, toX(b) - 8, pad.top + cH + 16);
  }

  // Axis titles
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font      = '12px monospace';
  ctx.fillText('Burns', pad.left + cW / 2 - 20, H - 5);

  ctx.save();
  ctx.translate(14, pad.top + cH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('Altitude (km)', -40, 0);
  ctx.restore();

  // Legend
  ctx.fillStyle = '#00ff88';
  ctx.font      = 'bold 12px monospace';
  ctx.fillText('— Apogee', pad.left + 10, pad.top + 14);
  ctx.fillStyle = '#00ddff';
  ctx.fillText('— Perigee', pad.left + 110, pad.top + 14);
  ctx.fillStyle = 'rgba(255,220,0,0.8)';
  ctx.fillText('- - 460 km target', pad.left + 220, pad.top + 14);

  // Chart title
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.font      = 'bold 13px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('APOGEE / PERIGEE vs BURN NUMBER', W / 2, pad.top - 10);
  ctx.textAlign = 'left';
}

// ─── Canvas Drawing: ΔV Budget ────────────────────────────────────────────────
function drawDVBudget(canvas, s) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W   = canvas.width;
  const H   = canvas.height;

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#030d1a';
  ctx.fillRect(0, 0, W, H);

  const pad  = { top: 36, right: 20, bottom: 50, left: 20 };
  const cW   = W - pad.left - pad.right;
  const cH   = H - pad.top  - pad.bottom;

  // Total DV for full transfer
  const dvTotal     = TOTAL_BURNS * DV_BURN;             // ~5.6 m/s
  const dvUsed      = s.dvTotal;

  // EP-ADCS propellant
  const propEP_mg   = s.propMg;                          // mg
  const propEP_full = M_SAT * (1 - Math.exp(-dvTotal / VE)) * 1e6; // mg for full transfer

  // Chemical equivalent (same DV, Isp=220s)
  const propChem_g  = M_SAT * (1 - Math.exp(-dvTotal / VE_CHEM)) * 1e3; // g for full transfer
  const propChem_used = M_SAT * (1 - Math.exp(-dvUsed / VE_CHEM)) * 1e3; // g used so far

  // Station-keeping: how many years with remaining propellant
  // Total propellant budget (let's say 10g for EP-ADCS system)
  const totalPropBudget_mg = 10000; // 10g = 10000mg
  const skPropPerYear_mg   = M_SAT * (1 - Math.exp(-SK_DV_PER_YEAR / VE)) * 1e6;
  const yearsRemaining     = (totalPropBudget_mg - propEP_mg) / skPropPerYear_mg;

  // Section layout: two bar areas + SK info
  const barSectionW = cW * 0.55;
  const infoSectionX = pad.left + barSectionW + 20;

  // Bar chart
  const barPad  = 30;
  const barW    = (barSectionW - barPad * 3) / 2;
  const maxH    = cH - 20;

  // Max scale: use chemical full as reference
  const maxVal  = propChem_g; // in g equivalent for scale

  // EP-ADCS bar (in mg, scale relative to chemical g)
  const epBarH_full = (propEP_full / 1e3 / maxVal) * maxH; // convert mg to g
  const epBarH_used = (propEP_mg   / 1e3 / maxVal) * maxH;
  const epBarX      = pad.left + barPad;
  const epBarY      = pad.top + maxH;

  // Chemical bar
  const chemBarH_full = maxH;
  const chemBarH_used = (propChem_used / maxVal) * maxH;
  const chemBarX      = pad.left + barPad * 2 + barW;
  const chemBarY      = pad.top + maxH;

  // Background bars (full)
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.fillRect(epBarX,   epBarY   - epBarH_full,   barW, epBarH_full);
  ctx.fillRect(chemBarX, chemBarY - chemBarH_full, barW, chemBarH_full);

  // EP-ADCS used bar
  const epGrad = ctx.createLinearGradient(0, epBarY - epBarH_used, 0, epBarY);
  epGrad.addColorStop(0, '#00ff88');
  epGrad.addColorStop(1, '#006633');
  ctx.save();
  ctx.fillStyle   = epGrad;
  ctx.shadowColor = '#00ff88';
  ctx.shadowBlur  = 10;
  ctx.fillRect(epBarX, epBarY - Math.max(epBarH_used, 2), barW, Math.max(epBarH_used, 2));
  ctx.restore();

  // Chemical used bar
  const chemGrad = ctx.createLinearGradient(0, chemBarY - chemBarH_used, 0, chemBarY);
  chemGrad.addColorStop(0, '#ff6600');
  chemGrad.addColorStop(1, '#661a00');
  ctx.save();
  ctx.fillStyle   = chemGrad;
  ctx.shadowColor = '#ff6600';
  ctx.shadowBlur  = 10;
  ctx.fillRect(chemBarX, chemBarY - Math.max(chemBarH_used, 0), barW, Math.max(chemBarH_used, 0));
  ctx.restore();

  // Bar outlines
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth   = 1;
  ctx.strokeRect(epBarX,   epBarY   - epBarH_full,   barW, epBarH_full);
  ctx.strokeRect(chemBarX, chemBarY - chemBarH_full, barW, chemBarH_full);

  // Labels above bars
  ctx.textAlign = 'center';
  ctx.fillStyle = '#00ff88';
  ctx.font      = 'bold 12px monospace';
  ctx.fillText('EP-ADCS', epBarX + barW / 2, pad.top + 14);
  ctx.fillStyle = '#aaffcc';
  ctx.font      = '11px monospace';
  ctx.fillText(`${propEP_mg.toFixed(3)} mg`, epBarX + barW / 2, pad.top + 26);

  ctx.fillStyle = '#ff6600';
  ctx.font      = 'bold 12px monospace';
  ctx.fillText('Chemical', chemBarX + barW / 2, pad.top + 14);
  ctx.fillStyle = '#ffccaa';
  ctx.font      = '11px monospace';
  ctx.fillText(`${propChem_used.toFixed(3)} g`, chemBarX + barW / 2, pad.top + 26);

  // X axis labels
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font      = '11px monospace';
  ctx.fillText(`ISP=${ISP}s`, epBarX + barW / 2, epBarY + 15);
  ctx.fillText(`ISP=${ISP_CHEM}s`, chemBarX + barW / 2, chemBarY + 15);

  // Ratio annotation
  const ratio = propChem_g / (propEP_full / 1e3);
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font      = '11px monospace';
  ctx.fillText(`${ratio.toFixed(0)}× more`, chemBarX + barW / 2, chemBarY + 28);

  ctx.textAlign = 'left';

  // X axis line
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth   = 1;
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top + maxH);
  ctx.lineTo(pad.left + barSectionW, pad.top + maxH);
  ctx.stroke();

  // Right panel: info
  const ix = infoSectionX;
  const iw = W - ix - pad.right;

  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.beginPath();
  ctx.roundRect(ix, pad.top, iw, cH, 6);
  ctx.fill();

  ctx.fillStyle = '#00ff88';
  ctx.font      = 'bold 13px monospace';
  ctx.fillText('PROPELLANT BUDGET', ix + 10, pad.top + 16);

  const lines = [
    { label: 'ΔV used:',       val: `${dvUsed.toFixed(3)} m/s`,            color: '#00ff88' },
    { label: 'EP total:',      val: `${propEP_mg.toFixed(3)} mg`,          color: '#00ff88' },
    { label: 'Chem equiv:',    val: `${propChem_used.toFixed(3)} g`,       color: '#ff8844' },
    { label: 'SK rate:',       val: `${SK_DV_PER_YEAR} m/s/yr`,           color: '#aaaaff' },
    { label: 'SK ΔV/yr prop:', val: `${skPropPerYear_mg.toFixed(1)} mg`,  color: '#aaaaff' },
    { label: 'Years SK:',      val: `${yearsRemaining.toFixed(1)} yr`,    color: '#ffdd00' },
  ];

  lines.forEach((ln, i) => {
    const ly = pad.top + 34 + i * 22;
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font      = '12px monospace';
    ctx.fillText(ln.label, ix + 10, ly);
    ctx.fillStyle = ln.color;
    ctx.font      = 'bold 12px monospace';
    ctx.fillText(ln.val, ix + 10 + 110, ly);
  });

  // Chart title
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.font      = 'bold 13px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('ΔV BUDGET: EP-ADCS vs CHEMICAL PROPULSION', W / 2, pad.top - 14);
  ctx.textAlign = 'left';
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EPADCSOrbitSim() {
  const [running, setRunning]   = useState(false);
  const [speed,   setSpeed]     = useState(10000);
  const [burnMode, setBurnMode] = useState('auto');
  const [tick,    setTick]      = useState(0); // force re-render for metrics

  const orbitRef    = useRef(null);
  const progressRef = useRef(null);
  const dvRef       = useRef(null);
  const stateRef    = useRef(makeOrbit());
  const rafRef      = useRef(null);
  const lastTRef    = useRef(null);

  // Animation loop
  const animate = useCallback((timestamp) => {
    if (!lastTRef.current) lastTRef.current = timestamp;
    const wallDt = Math.min((timestamp - lastTRef.current) / 1000, 0.05); // cap at 50ms
    lastTRef.current = timestamp;

    const simDt = wallDt * speed;
    const steps = Math.min(Math.ceil(simDt / 30), 200); // RK4 steps, max dt=30s each
    const dt    = simDt / steps;

    let s = stateRef.current;
    for (let i = 0; i < steps; i++) {
      s = stepOrbit(s, dt, burnMode);
      if (s.complete) break;
    }
    stateRef.current = s;

    // Draw canvases
    drawOrbit(orbitRef.current,    s);
    drawProgress(progressRef.current, s);
    drawDVBudget(dvRef.current,    s);

    setTick(t => t + 1); // update metrics panel

    if (!s.complete) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      setRunning(false);
    }
  }, [speed, burnMode]);

  useEffect(() => {
    if (running) {
      lastTRef.current = null;
      rafRef.current   = requestAnimationFrame(animate);
    } else {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [running, animate]);

  // Initial draw on mount
  useEffect(() => {
    const s = stateRef.current;
    drawOrbit(orbitRef.current,    s);
    drawProgress(progressRef.current, s);
    drawDVBudget(dvRef.current,    s);
  }, []);

  const handleReset = () => {
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    stateRef.current = makeOrbit();
    const s = stateRef.current;
    drawOrbit(orbitRef.current,    s);
    drawProgress(progressRef.current, s);
    drawDVBudget(dvRef.current,    s);
    setTick(0);
  };

  const s = stateRef.current;

  // Station-keeping years remaining
  const dvTotal     = TOTAL_BURNS * DV_BURN;
  const propEP_full = M_SAT * (1 - Math.exp(-dvTotal / VE)) * 1e6;
  const skPropPerYear = M_SAT * (1 - Math.exp(-SK_DV_PER_YEAR / VE)) * 1e6;
  const skYears     = ((10000 - s.propMg) / skPropPerYear).toFixed(1);

  // Chemical comparison
  const chemEquiv   = (M_SAT * (1 - Math.exp(-s.dvTotal / (ISP_CHEM * G0))) * 1e3).toFixed(3);

  return (
    <div style={{
      background:  '#020810',
      color:       '#e0f0ff',
      fontFamily:  'monospace',
      minHeight:   '100vh',
      padding:     '20px',
      boxSizing:   'border-box',
    }}>
      {/* Header */}
      <div style={{ marginBottom: 20, borderBottom: '1px solid #0a3050', paddingBottom: 16 }}>
        <h1 style={{ color: '#00ff88', margin: 0, fontSize: 26, letterSpacing: 2 }}>
          SPACEMANCRAFT-1 // EP-ADCS ORBITAL MANOEUVRE SIMULATOR
        </h1>
        <p style={{ color: '#4488aa', margin: '6px 0 0', fontSize: 14 }}>
          Electric Propulsion ADCS · SSO 450 km → 460 km Hohmann Transfer · 215-Burn Sequence
        </p>
      </div>

      {/* Controls */}
      <div style={{
        display:        'flex',
        gap:            14,
        alignItems:     'center',
        flexWrap:       'wrap',
        marginBottom:   18,
        padding:        '12px 16px',
        background:     '#030f1e',
        border:         '1px solid #0a2a40',
        borderRadius:   8,
      }}>
        <button
          onClick={() => setRunning(r => !r)}
          style={{
            background:   running ? '#003322' : '#002211',
            color:        '#00ff88',
            border:       '1px solid #00ff88',
            borderRadius: 5,
            padding:      '8px 24px',
            cursor:       'pointer',
            fontSize:     15,
            fontFamily:   'monospace',
            letterSpacing: 1,
            minWidth:     100,
          }}
        >
          {running ? '⏸ PAUSE' : '▶ RUN'}
        </button>
        <button
          onClick={handleReset}
          style={{
            background:   '#1a0500',
            color:        '#ff6644',
            border:       '1px solid #ff4422',
            borderRadius: 5,
            padding:      '8px 20px',
            cursor:       'pointer',
            fontSize:     15,
            fontFamily:   'monospace',
          }}
        >
          ↺ RESET
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#4488aa', fontSize: 13 }}>SPEED:</span>
          <input
            type="range" min={100} max={100000} step={100}
            value={speed}
            onChange={e => setSpeed(Number(e.target.value))}
            style={{ width: 160, accentColor: '#00ff88' }}
          />
          <span style={{ color: '#00ff88', fontSize: 14, minWidth: 80 }}>
            {speed >= 1000 ? `${(speed / 1000).toFixed(1)}k` : speed}× real
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#4488aa', fontSize: 13 }}>BURN MODE:</span>
          {['auto', 'phase1', 'phase2'].map(m => (
            <button
              key={m}
              onClick={() => setBurnMode(m)}
              style={{
                background:   burnMode === m ? '#003322' : 'transparent',
                color:        burnMode === m ? '#00ff88' : '#4488aa',
                border:       `1px solid ${burnMode === m ? '#00ff88' : '#0a3050'}`,
                borderRadius: 4,
                padding:      '5px 12px',
                cursor:       'pointer',
                fontSize:     12,
                fontFamily:   'monospace',
              }}
            >
              {m === 'auto' ? 'AUTO' : m === 'phase1' ? 'PHASE 1' : 'PHASE 2'}
            </button>
          ))}
        </div>

        {s.complete && (
          <span style={{ color: '#ffdd00', fontSize: 14, fontWeight: 'bold' }}>
            ✓ TRANSFER COMPLETE
          </span>
        )}
      </div>

      {/* Metrics Panel */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap:                 10,
        marginBottom:        18,
      }}>
        {[
          { label: 'Current Alt',     val: `${((Math.sqrt(s.x*s.x+s.y*s.y)-RE)/1e3).toFixed(2)} km`, color: '#00ff88' },
          { label: 'Apogee',          val: `${s.apogee.toFixed(2)} km`,    color: '#00ff88' },
          { label: 'Perigee',         val: `${s.perigee.toFixed(2)} km`,   color: '#00ddff' },
          { label: 'Burns Done',      val: `${s.burnCount} / ${TOTAL_BURNS}`, color: '#ffdd00' },
          { label: 'ΔV Used',         val: `${s.dvTotal.toFixed(4)} m/s`,  color: '#ff8844' },
          { label: 'EP Propellant',   val: `${s.propMg.toFixed(4)} mg`,    color: '#00ff88' },
          { label: 'Chem Equiv',      val: `${chemEquiv} g`,               color: '#ff6600' },
          { label: 'SK Budget',       val: `${skYears} years`,             color: '#aaaaff' },
        ].map(m => (
          <div key={m.label} style={{
            background:   '#030f1e',
            border:       '1px solid #0a2a40',
            borderRadius: 6,
            padding:      '10px 14px',
          }}>
            <div style={{ color: '#446688', fontSize: 11, marginBottom: 4 }}>{m.label}</div>
            <div style={{ color: m.color, fontSize: 20, fontWeight: 'bold' }}>{m.val}</div>
          </div>
        ))}
      </div>

      {/* Orbit Canvas */}
      <div style={{ marginBottom: 14, background: '#020810', borderRadius: 8, overflow: 'hidden', border: '1px solid #0a2a40' }}>
        <canvas
          ref={orbitRef}
          width={1200}
          height={700}
          style={{ width: '100%', display: 'block' }}
        />
      </div>

      {/* Progress Chart */}
      <div style={{ marginBottom: 14, background: '#020810', borderRadius: 8, overflow: 'hidden', border: '1px solid #0a2a40' }}>
        <canvas
          ref={progressRef}
          width={1000}
          height={220}
          style={{ width: '100%', display: 'block' }}
        />
      </div>

      {/* ΔV Budget Chart */}
      <div style={{ marginBottom: 20, background: '#020810', borderRadius: 8, overflow: 'hidden', border: '1px solid #0a2a40' }}>
        <canvas
          ref={dvRef}
          width={1000}
          height={220}
          style={{ width: '100%', display: 'block' }}
        />
      </div>

      {/* Info Panel */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap:                 14,
        marginBottom:        20,
      }}>
        {[
          {
            title: 'Hohmann Transfer',
            color: '#00ff88',
            body: `A Hohmann transfer moves a satellite between two circular orbits
using two burns at opposite points of a transfer ellipse.

Burn 1 at perigee → raises apogee
Burn 2 at apogee  → raises perigee / circularises

This is the most propellant-efficient 2-burn transfer.
Total ΔV for 450→460 km: ${(TOTAL_BURNS * DV_BURN).toFixed(2)} m/s`,
          },
          {
            title: 'Why 215 Burns?',
            color: '#ffdd00',
            body: `A classic Hohmann uses just 2 burns.

EP-ADCS generates only ${(J_BURN * 1000).toFixed(0)} mNs per burn
→ ΔV = ${DV_BURN.toFixed(5)} m/s each.

Each pass applies a tiny prograde kick.
108 perigee kicks raise the apogee gradually.
107 apogee kicks raise the perigee to circularise.
Total: 215 small burns ≈ 1 equivalent Hohmann.`,
          },
          {
            title: 'Atmospheric Drag',
            color: '#00ddff',
            body: `At 450 km, air density ρ ≈ 4.1×10⁻¹² kg/m³

Drag force: F = ½ρCd·A·v²
For SPACEMANCRAFT-1 (Cd=2.2, A=0.01 m², M=12 kg):

Annual drag ΔV loss ≈ 30 m/s/yr

Station keeping requires continuous small burns
to counteract drag. EP-ADCS uses only ~${skPropPerYear.toFixed(1)} mg/yr
of propellant for this.`,
          },
          {
            title: 'EP-ADCS Advantage',
            color: '#ff8844',
            body: `EP-ADCS Isp = 70 s → exhaust velocity 686.7 m/s
Chemical Isp = 220 s → exhaust velocity 2158 m/s

For the full 450→460 km transfer:
EP-ADCS uses:  ${propEP_full.toFixed(2)} mg
Chemical needs: ${(M_SAT*(1-Math.exp(-(TOTAL_BURNS*DV_BURN)/(ISP_CHEM*G0)))*1e3).toFixed(2)} g

That's a ${((M_SAT*(1-Math.exp(-(TOTAL_BURNS*DV_BURN)/(ISP_CHEM*G0)))*1e3) / (propEP_full/1e3)).toFixed(0)}× mass difference.
EP-ADCS is ideal for small satellites.`,
          },
        ].map(card => (
          <div key={card.title} style={{
            background:   '#030f1e',
            border:       `1px solid #0a2a40`,
            borderRadius: 8,
            padding:      '16px 18px',
            borderTop:    `2px solid ${card.color}`,
          }}>
            <h3 style={{ color: card.color, margin: '0 0 10px', fontSize: 15, letterSpacing: 1 }}>
              {card.title}
            </h3>
            <pre style={{
              color:       '#99bbcc',
              fontSize:    12,
              margin:      0,
              whiteSpace:  'pre-wrap',
              lineHeight:  1.6,
              fontFamily:  'monospace',
            }}>
              {card.body}
            </pre>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{
        display:    'flex',
        gap:        24,
        flexWrap:   'wrap',
        padding:    '10px 16px',
        background: '#030f1e',
        borderRadius: 6,
        border:     '1px solid #0a2a40',
        fontSize:   13,
      }}>
        <span><span style={{ color: '#00ff88' }}>●</span> Current orbit ellipse</span>
        <span><span style={{ color: 'orange' }}>●</span> Trail: outbound (vr&gt;0)</span>
        <span><span style={{ color: 'cyan' }}>●</span> Trail: inbound (vr&lt;0)</span>
        <span><span style={{ color: '#4488ff' }}>●</span> Perigee marker</span>
        <span><span style={{ color: '#00ff88' }}>●</span> Apogee marker</span>
        <span><span style={{ color: 'rgba(150,150,150,0.8)' }}>- -</span> 450 km reference</span>
        <span><span style={{ color: 'rgba(255,220,0,0.8)' }}>- -</span> 460 km target</span>
        <span><span style={{ color: '#ff6600' }}>→</span> Thrust impulse</span>
      </div>
    </div>
  );
}
